/**
 * Kanban column construction and client-side filtering for the entity kanban
 * (spec §10.2, patch D2).
 *
 * Given a flat list of entities (each carrying `currentStageNumber`), build
 * one column per selected stage. Stages 1-5 are the "batch" stages where
 * SUB_PARTs (and Variant B PARTs pre-aggregate-gate) live; stages 6-18
 * are the "part" stages where PARTs live.
 *
 * By default, columns are returned for EVERY stage 1-18, even empty ones, so
 * the kanban layout is consistent. The page can pass `visibleStages` to show
 * only a selected subset of columns without changing the underlying card
 * filters.
 *
 * Inside each column, items are returned in the order they were given — the
 * caller (or the server) decides sort order. The server currently sorts by
 * `daysInCurrentStage` DESC within stage so the most stale entities appear at
 * the top of each column.
 */

import { stageLabel } from '@procurement/shared';
import type { EntityKanbanItem } from './visibility-queries';

export const ENTITY_KANBAN_STAGE_NUMBERS = Array.from({ length: 18 }, (_, index) => index + 1);

export interface EntityKanbanColumn {
  stageNumber: number;
  stageLabel: string;
  items: EntityKanbanItem[];
}

export interface EntityKanbanFilters {
  q?: string;
  projectId?: string;
  kind?: 'PART' | 'SUB_PART';
  stage?: number;
  minDays?: number;
}

export interface EntityKanbanColumnOptions {
  /** undefined = all columns; [] = deliberately show no columns. */
  visibleStages?: readonly number[];
}

export interface EntityKanbanProjectOption {
  value: string;
  label: string;
  hint: string;
}

export function buildKanbanColumns(
  items: EntityKanbanItem[],
  options: EntityKanbanColumnOptions = {},
): EntityKanbanColumn[] {
  const byStage = new Map<number, EntityKanbanItem[]>();
  for (const item of items) {
    const arr = byStage.get(item.currentStageNumber) ?? [];
    arr.push(item);
    byStage.set(item.currentStageNumber, arr);
  }

  const stages = normalizeVisibleStages(options.visibleStages);
  return stages.map((stageNumber) => ({
    stageNumber,
    stageLabel: stageLabel(stageNumber),
    items: byStage.get(stageNumber) ?? [],
  }));
}

export function applyEntityKanbanFilters(
  items: EntityKanbanItem[],
  filters: EntityKanbanFilters,
): EntityKanbanItem[] {
  const q = normalizeSearch(filters.q ?? '');
  return items.filter((item) => {
    if (filters.projectId && item.projectId !== filters.projectId) return false;
    if (filters.kind && item.entityKind !== filters.kind) return false;
    if (filters.stage !== undefined && item.currentStageNumber !== filters.stage) return false;
    if (
      filters.minDays !== undefined &&
      Number.isFinite(filters.minDays) &&
      item.daysInCurrentStage < filters.minDays
    ) {
      return false;
    }

    if (q !== '') {
      const haystack = normalizeSearch(
        [
          item.itemName,
          item.parentItemName,
          item.projectNumber,
          item.projectTitle,
          item.currentStageLabel,
          String(item.currentStageNumber),
        ]
          .filter(Boolean)
          .join(' '),
      );
      if (!haystack.includes(q)) return false;
    }

    return true;
  });
}

export function getEntityKanbanProjectOptions(
  items: EntityKanbanItem[],
): EntityKanbanProjectOption[] {
  const byProject = new Map<string, EntityKanbanProjectOption>();
  for (const item of items) {
    if (byProject.has(item.projectId)) continue;
    byProject.set(item.projectId, {
      value: item.projectId,
      label: `${item.projectNumber} — ${item.projectTitle}`,
      hint: item.projectTitle,
    });
  }
  return [...byProject.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

export function parseVisibleStagesParam(value: string | undefined): number[] | undefined {
  if (value === undefined) return undefined;
  if (value === 'none') return [];
  const unique = new Set<number>();
  for (const rawPart of value.split(',')) {
    const n = Number(rawPart.trim());
    if (Number.isInteger(n) && n >= 1 && n <= 18) unique.add(n);
  }
  return [...unique].sort((a, b) => a - b);
}

export function serializeVisibleStagesParam(stages: readonly number[] | null): string | undefined {
  if (stages === null) return undefined;
  const normalized = normalizeVisibleStages(stages);
  if (normalized.length === 0) return 'none';
  return normalized.join(',');
}

function normalizeVisibleStages(stages: readonly number[] | undefined): number[] {
  if (stages === undefined) return ENTITY_KANBAN_STAGE_NUMBERS;
  const unique = new Set<number>();
  for (const n of stages) {
    if (Number.isInteger(n) && n >= 1 && n <= 18) unique.add(n);
  }
  return [...unique].sort((a, b) => a - b);
}

function normalizeSearch(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}
